import {
  Box,
  Flex,
  Grid,
  GridColumn,
  omitText,
  Typography,
} from '@kohler-dsm-library/kohler-core';
import TabbedNavigationHorizontal from '@/components/DesignSystem/TabbedNavigationHorizontal';
import _ from 'lodash';
import { ArrowLeftIcon, CloseIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React, { Fragment, useEffect, useState } from 'react';
import { OverlayMenuCategory } from '../OverlayMenuCategory';
import { OverlayMenuImage } from '../OverlayMenuImage';
import { OverlayMenuItem } from '../OverlayMenuItem';
import { OverlayMenuLinks } from '../OverlayMenuLinks';
import { OverlayMenuTabs } from '../OverlayMenuTabs';
import classes from './index.module.scss';
import { countCTAListsWithItems, isILink } from './index.utils';
import { UtilityBarMobile } from '../UtilityBarMobile';

const normalizeMenuLink = (link = '') => link.split(/[?#]/)[0].replace(/\/+$/, '');

const containsParentLink = (value, parentLink) => {
  if (Array.isArray(value)) {
    return value.some((item) => containsParentLink(item, parentLink));
  }

  if (!value || typeof value !== 'object') return false;

  if (typeof value.href === 'string' && normalizeMenuLink(value.href) === parentLink) {
    return true;
  }

  return Object.values(value).some((item) => containsParentLink(item, parentLink));
};

const hasRelevantMenu = (tab) => {
  if (!tab.menuItems?.flat()?.length) return false;
  if (!tab.parentLink) return true;

  return containsParentLink(tab.menuItems, normalizeMenuLink(tab.parentLink));
};

export const OverlayMenu = (props) => {
  const {
    className,
    CTALists,
    images,
    mainMenuItems,
    setIsOpenMobileMenu,
    isMoreOverlay,
    utilityLinks,
    buttonCTA,
    closeCTA,
    tabList,
    ...rest
  } = props;

  const [selectedTab, setSelectedTab] = useState(null);
  const [selectedMobileMenuItem, setSelectedMobileMenuItem] = useState(null);
  const [selectedMoreTab, setSelectedMoreTab] = useState(
    isMoreOverlay && mainMenuItems ? mainMenuItems[0] : null
  );
  const [CTAListsData, setCTAListsData] = useState(tabList?.length > 0 ? tabList?.find(tab => tab.title === tabList?.[0]?.title)?.menuItems : CTALists ?? []);
  const [openCategoryIndex, setOpenCategoryIndex] = useState(null);

  const menueprops = {
    align: "left",
    initialSelectedTab: tabList?.[0]?.title || null,
    tabs: tabList?.map((tab, index) => ({
      text: tab.title,
      id: `label-${index + 1}`,
      parentLink: tab.parentLink,
      hasMenu: hasRelevantMenu(tab),
    })),
  };

  const CTAListsMobile = mainMenuItems?.find((val) => val.text === selectedMobileMenuItem);

  useEffect(() => {
    setSelectedTab(tabList?.[0]?.title || null)
  }, [tabList]);

  useEffect(() => {
    if (isMoreOverlay) {
      setSelectedMoreTab(mainMenuItems ? mainMenuItems[0] : null);
    }
  }, [isMoreOverlay, mainMenuItems]);

  useEffect(() => {
    if (selectedTab === '') {
      setCTAListsData([]);
      return;
    }

    const data = tabList?.find(tab => tab.title === selectedTab);
    setCTAListsData(data?.menuItems || CTALists);
  }, [selectedTab, tabList, CTALists]);

  // Find the selected tab object in tabList for desktop images
  const selectedTabData = tabList?.find(tab => tab.title === selectedTab);

  // Determine which images to show (tab images or fallback to top-level images)
  const imagesToShow =
    selectedTab === ''
      ? []
      : (selectedTabData?.images?.length > 0 && selectedTabData.images) ||
        (images?.length > 0 && images) ||
        [];

  let shopAllLinkMobile = null;
  if (CTAListsMobile) {
    if (selectedTab && CTAListsMobile.tabList) {
      // Find the selected tab object
      const tabData = CTAListsMobile.tabList.find(tab => tab.title === selectedTab);
      if (tabData && tabData.menuItems) {
        const flatMenuItems = tabData.menuItems.flat();
        shopAllLinkMobile = flatMenuItems
          .flatMap(item => item.items || [])
          .find(link =>
            (link.text && link.text.toLowerCase().includes('shop all')) ||
            (link.label && link.label.toLowerCase().includes('shop all'))
          );
      }
    }
    // Fallback: if no tab selected or no Shop All in tab, check main menu item's CTALists
    if (!shopAllLinkMobile) {
      const allLinks = (CTAListsMobile.CTALists || [])
        .flatMap(list =>
          Array.isArray(list)
            ? list.flatMap(l => l.items || [])
            : (list.items || [])
        );
      shopAllLinkMobile = allLinks.find(
        link =>
          (link.text && link.text.toLowerCase().includes('shop all')) ||
          (link.label && link.label.toLowerCase().includes('shop all'))
      );
    }
  }

  // MOBILE: If a tab is selected, show its submenu categories as accordions
  const renderMobileTabContent = () => {
    if (!CTAListsMobile || !selectedTab) return null;

    // Find the selected tab object in tabList
    const tabData = CTAListsMobile.tabList?.find(tab => tab.title === selectedTab);
    if (!tabData || !tabData.menuItems || tabData.menuItems.length === 0) return (
      <Typography size="navigation-m" textColor="var(--content-secondary)" style={{ padding: '24px', textAlign: 'center' }}>
        No menu items found.
      </Typography>
    );
    const flatMenuItems = tabData.menuItems.flat();


    return (
      <>
        <Flex className={classes.overlayMenuLinkLevelOneMobile}>
          <Box
            component="button"
            type="button"
            onClick={() => setSelectedTab(null)}
            className={closeCTA?.className}
          >
            <ArrowLeftIcon size="small" withBoundingBox={true} className={classes.arrowLeftIcon} />
          </Box>
              <Typography
                size="navigation-overlay-item-s"
                textColor="var(--content-primary)"
                width="100%"
                className={classes.overlayMenuMobileTitle}
              >
                {CTAListsMobile.text} / {selectedTab}
              </Typography>
          <Box
            component="button"
            type="button"
            {...closeCTA}
            onClick={() => setIsOpenMobileMenu && setIsOpenMobileMenu(false)}
            className={clsx(classes.overlayMenuLinkCloseButtonMobile, closeCTA?.className)}
          >
            <CloseIcon size="medium" withBoundingBox={false} />
          </Box>
        </Flex>

        {/* Submenu categories as accordions */}
        <Flex direction="column" gap={20}>
          {flatMenuItems.map((item, idx) => (
            <OverlayMenuCategory
              key={item.id || item.text || idx}
              title={item.title}
              links={item.items?.filter(isILink) || []}
              className={classes.overlayMenuCategoryTitle}
              opened={openCategoryIndex === idx}
              onToggle={() => setOpenCategoryIndex(openCategoryIndex === idx ? null : idx)}
            />
          ))}
        </Flex>

        {/* Images for the selected tab, if any */}
        {(tabData.images?.length > 0 || images?.length > 0) && (
          <Flex className={classes.overlayMenuMobileImages}>
            {(tabData.images?.length > 0 ? tabData.images : images || []).map((image) => (
              <OverlayMenuImage
                key={image.image.src}
                imagesLength={(tabData.images?.length > 0 ? tabData.images : images || []).length}
                isBusinessTab={!['Bathroom', 'Collection', 'Kitchen', 'Lighting', 'Wellness'].includes(rest?.text || '')}
                {...image}
              />
            ))}
          </Flex>
        )}


        {/* CTA for the selected tab, if any */}
        {tabData.buttonCTA && (
          <Typography
            type={tabData.buttonCTA.onClick ? 'button' : undefined}
            component={tabData.buttonCTA.href ? 'a' : 'button'}
            size="navigation-l"
            textColor="var(--content-primary)"
            {...omitText(tabData.buttonCTA)}
          >
            {tabData.buttonCTA.text}
          </Typography>
        )}
      </>
    );
  };

  return (
    <Box
      className={clsx(
        className,
        'kohler-overlay-menu-root',
        classes.overlayMenu,
        selectedTab === '' && classes.overlayMenuEmpty
      )}
      {...rest}
    >
      {tabList?.length > 0 && (
        <TabbedNavigationHorizontal {...menueprops} onTabChange={(tab) => {
          setSelectedTab(tab.hasMenu === false ? '' : tab.text);
          setOpenCategoryIndex(null);
        }} />
      )}
      <Grid className={classes.overlayMenuLinkGridMainMobileWrapper}>
        {/* DESKTOP OVERLAY MENU */}
        <GridColumn className={classes.overlayMenuLinkGridWrapper}>
          {(CTALists || (isMoreOverlay && mainMenuItems)) && (
            <Flex className={classes.overlayMenuLinkGridLists}>
              {isMoreOverlay && (
                <OverlayMenuTabs
                  className={classes.overlayMenuLinkGrid}
                  CTAs={mainMenuItems?.map((mainMenuItem) => ({
                    text: mainMenuItem.text,
                    href: mainMenuItem.href,
                    onClick: () => setSelectedMoreTab(mainMenuItem),
                    isSelected: selectedMoreTab?.text === mainMenuItem.text,
                    hasChildrenList: !!mainMenuItem.CTALists,
                  }))}
                />
              )}
              {(selectedMoreTab?.CTALists || CTAListsData)?.map((CTAList, index) => {
                if (Array.isArray(CTAList)) {
                  return (
                    <Flex direction="column" gap="40px" key={`select-more-tab-cta-index-${index}`}>
                      {CTAList.map((overlayMenuLinkGroup, idx) => (
                        <OverlayMenuLinks
                          key={overlayMenuLinkGroup?.title?.text + `overlay-menu-${idx}`}
                          className={classes.overlayMenuLinkGrid}
                          {...overlayMenuLinkGroup}
                        />
                      ))}
                    </Flex>
                  );
                } else {
                  return (
                    <Fragment key={CTAList?.title?.text + `overlay-menu-${index}`}>
                      {isMoreOverlay && index % 3 === 0 && index !== 0 && <Box className={classes.overlayMenuLinkGrid} />}
                      <OverlayMenuLinks className={classes.overlayMenuLinkGrid} {...CTAList} />
                    </Fragment>
                  );
                }
              })}
            </Flex>
          )}
          {/* Updated image rendering logic with fallback */}
          {imagesToShow.length > 0 && (
            <Flex gap={30}>
              {imagesToShow.map((image) => (
                <OverlayMenuImage
                  key={image.image.src}
                  imagesLength={imagesToShow.length}
                  isBusinessTab={!['Bathroom', 'Collection', 'Kitchen', 'Lighting', 'Wellness'].includes(rest?.text || '')}
                  {...image}
                />
              ))}
            </Flex>
          )}
        </GridColumn>

        {/* MOBILE OVERLAY MENU */}
        <GridColumn className={classes.overlayMenuLinkGridMobileWrapper}>
          {/* Initial mobile menu: show main menu items (Tile, Slabs, etc.) */}
          {mainMenuItems && !selectedMobileMenuItem && !selectedTab && (
            <>
              <Box
                component="button"
                type="button"
                {...closeCTA}
                onClick={(e) => {
                  if (setIsOpenMobileMenu) setIsOpenMobileMenu(false);
                  if (closeCTA?.onClick) closeCTA.onClick(e);
                }}
                className={clsx(classes.overlayMenuLinkCloseButtonMobile, closeCTA?.className)}
              >
                <CloseIcon size="medium" withBoundingBox={false} />
              </Box>

              <Flex className={classes.overlayMenuLinkGridListsMobile}>
                {mainMenuItems.map((mainMenuItem, index) => (
                  <Fragment key={`${mainMenuItem.text}-mobile-${index}`}>
                    <OverlayMenuItem
                      title={mainMenuItem.text}
                      component={mainMenuItem.CTALists ? 'button' : 'a'}
                      href={mainMenuItem.CTALists ? undefined : mainMenuItem.href}
                      onClick={
                        mainMenuItem.CTALists
                          ? (e) => {
                            setSelectedMobileMenuItem(mainMenuItem.text);
                            setOpenCategoryIndex(null);
                            mainMenuItem.onClick?.(e);
                          }
                          : undefined
                      }
                      className={mainMenuItem.className}
                    />
                    {index < mainMenuItems.length - 1 && (
                      <div className={classes.menuItemDivider} />
                    )}
                  </Fragment>
                ))}
              </Flex>
            </>
          )}

          {/* When a main menu item is selected, show its tab bar (e.g., Tile -> Ceramic, Stone, ...) */}
          {CTAListsMobile && selectedMobileMenuItem && !selectedTab && CTAListsMobile.tabList?.length > 0 && (
            <>
              <Flex className={classes.overlayMenuLinkLevelOneMobile}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedMobileMenuItem(null)}
                  className={closeCTA?.className}
                >
                  <ArrowLeftIcon size="small" withBoundingBox={true} className={classes.arrowLeftIcon} />
                </Box>
                <Typography
                  size="navigation-overlay-item-s"
                  textColor="var(--Primary-Charcoal,#212121))"
                  width="100%"
                  className={classes.overlayMenuMobileTitle}
                >
                  {selectedMobileMenuItem}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  {...closeCTA}
                  onClick={() => setIsOpenMobileMenu && setIsOpenMobileMenu(false)}
                  className={clsx(classes.overlayMenuLinkCloseButtonMobile, closeCTA?.className)}
                >
                  <CloseIcon size="medium" withBoundingBox={false} />
                </Box>
              </Flex>
              <TabbedNavigationHorizontal
                tabs={CTAListsMobile.tabList.map((tab, idx) => ({
                  text: tab.title,
                  id: `tab-${idx}`,
                  parentLink: tab.parentLink,
                  hasMenu: hasRelevantMenu(tab),
                }))}
                align="left"
                defaultActiveTab={CTAListsMobile.tabList[0].title}
                onTabChange={tab => {
                  setSelectedTab(tab.hasMenu === false ? '' : tab.text);
                  setOpenCategoryIndex(null);
                }}
              />
            </>
          )}

          {/* When a tab is selected, show its submenu categories as accordions */}
          {CTAListsMobile && selectedTab && renderMobileTabContent()}

          {/* Fallback for menu items without tabList (other overlays) */}
          {CTAListsMobile && selectedMobileMenuItem && !CTAListsMobile.tabList?.length && (
            <>
              <Flex className={classes.overlayMenuLinkLevelOneMobile}>
                <Box
                  component="button"
                  type="button"
                  onClick={() => setSelectedMobileMenuItem(null)}
                  className={closeCTA?.className}
                  aria-label="Back"
                >
                  <ArrowLeftIcon size="small" withBoundingBox={true} className={classes.arrowLeftIcon} />
                </Box>
                <Typography
                  className={classes.overlayMenuTitleText}
                  size="navigation-overlay-item-s"
                  textColor="var(--Primary-Charcoal, #212121);"
                  width="100%"
                  font ="lato-regular"
                >
                  {selectedMobileMenuItem}
                </Typography>
                <Box
                  component="button"
                  type="button"
                  {...closeCTA}
                  onClick={() => setIsOpenMobileMenu && setIsOpenMobileMenu(false)}
                  className={clsx(classes.overlayMenuLinkCloseButtonMobile, closeCTA?.className)}
                  aria-label="Close"
                >
                  <CloseIcon size="medium" withBoundingBox={false} />
                </Box>
              </Flex>
              <Flex direction="column" gap={20}>
                {CTAListsMobile.CTALists?.map((CTAList, index) => {
                  if (Array.isArray(CTAList)) {
                    return (
                      <Flex direction="column" key={`cta-list-array-${index}`}>
                        {CTAList.map((CTAs, subIndex) => {
                          const flatIndex = index * 100 + subIndex;
                          const hasItems = CTAs.items?.filter(isILink);
                          return (
                            <Fragment key={CTAs?.title?.text + `overlay-category-${subIndex}`}>
                              {CTAs.title && (
                                <OverlayMenuCategory
                                  title={CTAs.title}
                                  isCTA={CTAs.isCTA}
                                  links={hasItems || []}
                                  opened={hasItems ? openCategoryIndex === flatIndex : undefined}
                                  onToggle={hasItems ? () => setOpenCategoryIndex(openCategoryIndex === flatIndex ? null : flatIndex) : undefined}
                                  className={CTAs.className}
                                />
                              )}
                            </Fragment>
                          );
                        })}
                      </Flex>
                    );
                  }

                  return (
                    <Fragment key={CTAList?.title?.text || `overlay-category-${index}`}>
                      {CTAList.title ? (
                        <OverlayMenuCategory
                          title={CTAList.title}
                          isCTA={CTAList.isCTA}
                          links={CTAList.items?.filter(isILink) || []}
                          opened={CTAList.items?.filter(isILink) && openCategoryIndex === index}
                          onToggle={
                            CTAList.items?.filter(isILink)
                              ? () => setOpenCategoryIndex(openCategoryIndex === index ? null : index)
                              : undefined
                          }
                          className={CTAList.className}
                        />
                      ) : (
                        CTAList.items?.map((CTAs, idx) => (
                          <Fragment key={`${CTAs?.title?.text || 'no-title'}-overlay-category-${idx}`}>
                            {CTAs.title && (
                              <OverlayMenuCategory
                                title={CTAs.title}
                                links={CTAs.items?.filter(isILink)}
                                showArrow={false}
                                isExternal={CTAs.newTabGroup || CTAs.title?.target === '_blank'}
                              />
                            )}
                          </Fragment>
                        ))
                      )}
                    </Fragment>
                  );
                })}
              </Flex>
              
              {CTAListsMobile.images?.length > 0 && (
                <Flex className={classes.overlayMenuMobileImages}>
                  {CTAListsMobile.images.map((image) => (
                    <OverlayMenuImage
                      key={image.image.src}
                      imagesLength={CTAListsMobile.images.length}
                      isBusinessTab={!['Bathroom', 'Collection', 'Kitchen', 'Lighting', 'Wellness'].includes(CTAListsMobile.text || '')}
                      {...image}
                    />
                  ))}
                </Flex>
              )}
            </>
          )}
        </GridColumn>
      </Grid>

      {/*Shop All CTA for mobile view */}
      {shopAllLinkMobile && (
        <div className={clsx(classes.shopAllCTA, classes.shopAllCTAFixed)}>
          <a
            href={shopAllLinkMobile.href || shopAllLinkMobile.link}
            target={shopAllLinkMobile.target}
            className={classes.shopAllCTA}
            {...omitText(shopAllLinkMobile)}
          >
            {shopAllLinkMobile.text || shopAllLinkMobile.label}
          </a>
        </div>
      )}

      {mainMenuItems && !selectedMobileMenuItem && <UtilityBarMobile utilityLinks={utilityLinks} 
      requestAppointmentCTA={buttonCTA} />}
    </Box>
  );
};